import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import Layout from '../layout/Layout';
import { getCurrentUser } from '../../services/authService';
import { supplierService } from '../../services/supplierService';

const Suppliers = () => {
  const user = getCurrentUser();
  const companyId = user?.companyId || 1;

  // State management
  const [suppliers, setSuppliers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('name');
  const [formValues, setFormValues] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    notes: '',
    company_id: companyId
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load suppliers
  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getAllSuppliers();
      // Handle paginated response
      setSuppliers(response.content || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load suppliers',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Filter suppliers based on search
  const filteredSuppliers = Array.isArray(suppliers) ? suppliers.filter(supplier => {
    const searchValue = searchQuery.toLowerCase();
    const fieldValue = supplier[searchField]?.toLowerCase() || '';
    return fieldValue.includes(searchValue);
  }) : [];

  // Dialog handlers
  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setSelectedSupplier(supplier);
      setFormValues({
        name: supplier.name || '',
        contact_person: supplier.contactPerson || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        website: supplier.website || '',
        address: supplier.address || '',
        notes: supplier.notes || '',
        company_id: companyId
      });
    } else {
      setSelectedSupplier(null);
      setFormValues({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        notes: '',
        company_id: companyId
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSupplier(null);
  };

  // Delete dialog handlers
  const handleOpenDeleteDialog = (supplier) => {
    setSupplierToDelete(supplier);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSupplierToDelete(null);
  };

  // Form submission handler
  const handleSubmit = async () => {
    try {
      console.log('Submitting supplier data:', formValues);
      
      const supplierData = {
        name: formValues.name.trim(),
        email: formValues.email.trim(),
        phone: formValues.phone.trim(),
        contactPerson: formValues.contact_person.trim(),
        address: formValues.address.trim(),
        notes: formValues.notes?.trim() || null,
        website: formValues.website?.trim() || null,
        companyId: user?.companyId || 1
      };
      
      console.log('Sending to API:', supplierData);

      if (selectedSupplier) {
        await supplierService.updateSupplier(selectedSupplier.id, supplierData);
        setSnackbar({
          open: true,
          message: 'Supplier updated successfully',
          severity: 'success'
        });
      } else {
        await supplierService.createSupplier(supplierData);
        setSnackbar({
          open: true,
          message: 'Supplier created successfully',
          severity: 'success'
        });
      }

      handleCloseDialog();
      loadSuppliers();
    } catch (error) {
      console.error('Error submitting supplier:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${selectedSupplier ? 'update' : 'create'} supplier: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  // Delete handler
  const handleDeleteSupplier = async () => {
    try {
      await supplierService.deleteSupplier(supplierToDelete.id);
      setSnackbar({
        open: true,
        message: 'Supplier deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadSuppliers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to delete supplier: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Snackbar handler
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Suppliers Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Supplier
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={`Search by ${searchField}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <Button
              variant="outlined"
              onClick={() => setSearchField(searchField === 'name' ? 'email' : 'name')}
            >
              Search by: {searchField === 'name' ? 'Name' : 'Email'}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((supplier, index) => (
                    <TableRow key={supplier.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>{supplier.contactPerson}</TableCell>
                      <TableCell>{supplier.email}</TableCell>
                      <TableCell>{supplier.phone}</TableCell>
                      <TableCell>{supplier.website || '-'}</TableCell>
                      <TableCell>{supplier.address}</TableCell>
                      <TableCell>{supplier.notes || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleOpenDialog(supplier)} size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleOpenDeleteDialog(supplier)} size="small">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredSuppliers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                required
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="Contact Person"
                name="contact_person"
                value={formValues.contact_person}
                onChange={handleInputChange}
                required
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formValues.email}
                onChange={handleInputChange}
                required
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formValues.phone}
                onChange={handleInputChange}
                required
                inputProps={{ maxLength: 20 }}
              />
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formValues.website}
                onChange={handleInputChange}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={2}
                value={formValues.address}
                onChange={handleInputChange}
                required
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={2}
                value={formValues.notes}
                onChange={handleInputChange}
                inputProps={{ maxLength: 255 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {selectedSupplier ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
        >
          <DialogTitle>Delete Supplier</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this supplier? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDeleteSupplier} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Suppliers; 